<template>
  <div class="antennes-radar-chart">
    <div class="chart-header">
      <span class="chart-title">Performance Surface Déclassée</span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading" />
      <p>Chargement des données...</p>
    </div>
    <div v-else class="chart-content">
      <div class="chart-wrapper">
        <Doughnut
          :chart-data="chartData"
          :chart-options="chartOptions"
          :height="null"
        />
      </div>
      <div v-if="chartData.labels && chartData.labels.length > 0" class="custom-legend">
        <div
          v-for="(label, index) in chartData.labels"
          :key="index"
          class="legend-item"
        >
          <span
            class="legend-color"
            :style="{ backgroundColor: getLegendColor(index) }"
          />
          <span class="legend-label">{{ getLegendLabel(index) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { mapGetters } from 'vuex';
import DashboardAPI from '@/api/dashboard';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

export default {
  name: 'AntennesRadarChart',
  components: {
    Doughnut,
  },
  data() {
    return {
      loading: false,
      kpiRequestsCompleted: true, // Par défaut true pour permettre le chargement initial
      chartData: {
        labels: [],
        datasets: [],
        rawData: [],
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters']),
    ...mapGetters('camp_agricole', ['camp_id']),
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 10,
            right: 20,
            left: 20,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const rawData = this.chartData.rawData;
                if (rawData && rawData[context.dataIndex] !== undefined) {
                  const rawValue = rawData[context.dataIndex];
                  const total = rawData.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((rawValue / total) * 100).toFixed(1) : 0;
                  return `${label}: ${rawValue.toFixed(2)} ha (${percentage}%)`;
                }
                return `${label}: ${value.toFixed(2)} ha`;
              },
            },
          },
        },
      };
    },
  },
  watch: {
    filters: {
      handler() {
        // Attendre que les requêtes KPI soient terminées avant de faire les requêtes des graphiques
        if (this.kpiRequestsCompleted) {
          this.fetchData();
        }
      },
      deep: true,
    },
    camp_id: {
      handler() {
        // Attendre que les requêtes KPI soient terminées avant de faire les requêtes des graphiques
        if (this.kpiRequestsCompleted) {
          this.fetchData();
        }
      },
    },
  },
  mounted() {
    // Écouter l'événement indiquant que les requêtes KPI sont terminées
    this.$root.$on('kpi-requests-completed', this.onKpiRequestsCompleted);
    // Écouter l'événement indiquant que les filtres sont appliqués (pour bloquer les requêtes)
    this.$root.$on('filters-applied', this.onFiltersApplied);
    // Écouter l'événement indiquant que les filtres commencent à être appliqués
    this.$root.$on('kpi-filters-loading', this.onKpiFiltersLoading);
    // Charger les données initiales
    this.fetchData();
  },
  beforeDestroy() {
    this.$root.$off('kpi-requests-completed', this.onKpiRequestsCompleted);
    this.$root.$off('filters-applied', this.onFiltersApplied);
    this.$root.$off('kpi-filters-loading', this.onKpiFiltersLoading);
  },
  methods: {
    onKpiRequestsCompleted() {
      this.kpiRequestsCompleted = true;
      // Faire les requêtes maintenant que les requêtes KPI sont terminées
      this.fetchData();
    },
    onFiltersApplied() {
      // Les filtres ont été appliqués, mais attendre que les requêtes KPI soient terminées
      // L'événement kpi-requests-completed sera émis après
    },
    onKpiFiltersLoading(data) {
      // Bloquer les requêtes des graphiques pendant que les requêtes KPI sont en cours
      if (data && data.loading) {
        this.kpiRequestsCompleted = false;
      }
    },
    buildApiFilters() {
      const activeFilters = this.filters || {};
      const niveau = activeFilters.niveau || {};

      return {
        campagne_id: this.camp_id || null,
        periode: {
          type: activeFilters.periode?.type || 'campagne',
          date_debut: activeFilters.periode?.date_debut || null,
          date_fin: activeFilters.periode?.date_fin || null,
        },
        ci_id: niveau.ci_id !== undefined && niveau.ci_id !== null ? niveau.ci_id : null,
        antenne_id: niveau.antenne_id !== undefined && niveau.antenne_id !== null ? niveau.antenne_id : null,
        groupement_id: niveau.groupement_id !== undefined && niveau.groupement_id !== null ? niveau.groupement_id : null,
        producteur_id: niveau.producteur_id !== undefined && niveau.producteur_id !== null ? niveau.producteur_id : null,
        cultures: (activeFilters.cultures || [])
          .map(id => {
            if (typeof id === 'number') {
              return id;
            }
            if (typeof id === 'object' && id !== null && id.id) {
              return typeof id.id === 'number' ? id.id : parseInt(id.id, 10);
            }
            if (typeof id === 'string') {
              const numId = parseInt(id, 10);
              return isNaN(numId) ? null : numId;
            }
            return id;
          })
          .filter(id => id !== null && id !== undefined && !isNaN(id)),
        certification: activeFilters.certification || 'toutes',
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const apiFilters = this.buildApiFilters();

        const response = await DashboardAPI.getPerformanceAntennesChart(apiFilters);
        if (response && response.data) {
          // Convertir les données pour le format Doughnut
          const labels = response.data.labels || [];
          const rawData = response.data.rawData || [];

          // Générer des couleurs pour toutes les antennes
          const colors = this.generateColors(labels.length);

          // Pour un Doughnut, on a besoin d'un seul dataset avec les valeurs brutes
          const datasets = [{
            label: 'Surface Déclassée (ha)',
            data: rawData,
            backgroundColor: colors.backgroundColor,
            borderColor: colors.borderColor,
            borderWidth: 2,
          }];

          this.chartData = {
            labels: labels,
            datasets: datasets,
            rawData: rawData,
          };
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de performance des antennes:', error);
        this.$message.error('Erreur lors du chargement des données de performance des antennes');
      } finally {
        this.loading = false;
      }
    },
    generateColors(count) {
      const baseColors = [
        { bg: 'rgba(245, 108, 108, 0.8)', border: '#f56c6c' },
        { bg: 'rgba(230, 162, 60, 0.8)', border: '#e6a23c' },
        { bg: 'rgba(144, 147, 153, 0.8)', border: '#909399' },
        { bg: 'rgba(103, 194, 58, 0.8)', border: '#67c23a' },
        { bg: 'rgba(64, 158, 255, 0.8)', border: '#409eff' },
        { bg: 'rgba(247, 137, 137, 0.8)', border: '#f78989' },
        { bg: 'rgba(149, 212, 117, 0.8)', border: '#95d475' },
        { bg: 'rgba(0, 171, 83, 0.8)', border: '#00ab53' },
        { bg: 'rgba(255, 159, 64, 0.8)', border: '#ff9f40' },
        { bg: 'rgba(153, 102, 255, 0.8)', border: '#9966ff' },
        { bg: 'rgba(255, 99, 132, 0.8)', border: '#ff6384' },
        { bg: 'rgba(54, 162, 235, 0.8)', border: '#36a2eb' },
        { bg: 'rgba(255, 206, 86, 0.8)', border: '#ffce56' },
        { bg: 'rgba(75, 192, 192, 0.8)', border: '#4bc0c0' },
        { bg: 'rgba(255, 159, 64, 0.8)', border: '#ff9f40' },
        { bg: 'rgba(201, 203, 207, 0.8)', border: '#c9cbcf' },
      ];

      const backgroundColor = [];
      const borderColor = [];

      for (let i = 0; i < count; i++) {
        const color = baseColors[i % baseColors.length];
        backgroundColor.push(color.bg);
        borderColor.push(color.border);
      }

      return { backgroundColor, borderColor };
    },
    getLegendColor(index) {
      if (this.chartData.datasets && this.chartData.datasets[0] && this.chartData.datasets[0].backgroundColor) {
        const colors = this.chartData.datasets[0].backgroundColor;
        if (Array.isArray(colors)) {
          return colors[index % colors.length];
        }
        return colors;
      }
      return '#909399';
    },
    getLegendLabel(index) {
      if (!this.chartData || !this.chartData.labels || !this.chartData.labels.length) {
        return '';
      }
      const label = this.chartData.labels[index] || '';

      if (!this.chartData.rawData || !Array.isArray(this.chartData.rawData)) {
        return label;
      }

      const values = this.chartData.rawData.map(v => Number(v) || 0);
      const total = values.reduce((sum, v) => sum + v, 0);

      if (!total) {
        return label;
      }

      const value = values[index] || 0;
      const percentage = ((value / total) * 100).toFixed(0);

      return `${label} (${percentage}%)`;
    },
  },
};
</script>

<style lang="scss" scoped>
.antennes-radar-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chart-header {
    background-color: #00ab53;
    color: #fff;
    padding: 12px 16px;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;

    .chart-title {
      font-size: 16px;
      font-weight: bold;
      display: block;
    }
  }

  .chart-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: row;
    gap: 15px;
    padding: 10px;
    overflow: hidden;
  }

  .chart-wrapper {
    flex: 1;
    min-width: 0;
    position: relative;
    overflow: hidden;
    max-width: 60%;

    ::v-deep canvas {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .custom-legend {
    flex: 0 0 260px;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 5px 10px 5px 5px;
    font-size: 10px;
    line-height: 1.4;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: auto;
    column-gap: 10px;
    row-gap: 6px;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;

      &:hover {
        background: #555;
      }
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;

      .legend-color {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        border-radius: 2px;
        display: inline-block;
      }

      .legend-label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #606266;
      }
    }
  }

  .chart-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

